import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { DETTAGLI, FAMIGLIE } from "../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const COSTI_LABELS = {
  cod_consegnato: "Andata — COD",
  prepagato_consegnato: "Andata — prepagato",
  annullato_prima: "Annullato prima della spedizione (nessun costo)",
  annullato_dopo_rientro: "Rientro (solo ritorno)",
  riconsegna: "Riconsegna (evento aggiuntivo)",
  giacenza: "Giacenza (evento aggiuntivo)",
};

const normCosti = (cs) => Object.fromEntries(Object.entries(cs || {}).map(([k, v]) => [
  k,
  v != null && typeof v === "object"
    ? { addebitato: v.addebitato ?? 0, reale: v.reale ?? v.addebitato ?? 0 }
    : { addebitato: v ?? 0, reale: v ?? 0 },
]));

const applicaRicarico = (reale, regola) => {
  if (!regola || !regola.tipo || regola.tipo === "manuale") return null;
  const r = reale || 0, v = regola.valore || 0;
  if (regola.tipo === "fisso") return Math.round((r + v) * 100) / 100;
  if (regola.tipo === "percentuale") return Math.round(r * (1 + v / 100) * 100) / 100;
  if (regola.tipo === "arrotondamento" && v > 0) return Math.round(Math.ceil(r / v - 1e-9) * v * 100) / 100;
  return null;
};

const STATO_INT = {
  connessa: { label: "Connessa", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  errore: { label: "Errore", cls: "bg-red-50 text-red-700 border-red-200" },
  non_configurata: { label: "Non configurata", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

const StatoPill = ({ st, testid }) => {
  if (!st) return null;
  const m = STATO_INT[st.stato] || STATO_INT.non_configurata;
  return (
    <span data-testid={testid}
      title={[st.verificata_il ? `Ultimo controllo: ${new Date(st.verificata_il).toLocaleString("it-IT")}` : null,
              st.messaggio, st.campi_mancanti?.length ? `Mancano: ${st.campi_mancanti.join(", ")}` : null]
              .filter(Boolean).join(" — ")}
      className={`text-[10px] font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 whitespace-nowrap ${m.cls}`}>
      {m.label}
    </span>
  );
};

const StatoDettaglio = ({ st, testid }) => {
  if (!st) return null;
  const parti = [];
  if (st.verificata_il) parti.push(`Ultimo controllo: ${new Date(st.verificata_il).toLocaleString("it-IT")}`);
  if (st.stato === "errore" && st.messaggio) parti.push(st.messaggio);
  if (st.campi_mancanti?.length) parti.push(`Mancano: ${st.campi_mancanti.join(", ")}`);
  if (!st.verificata_il && !st.campi_mancanti?.length) parti.push(st.messaggio || "Mai verificata");
  return <div data-testid={testid} className="text-[11px] text-slate-400 mt-0.5">{parti.join(" — ")}</div>;
};

function NuovoStoreDialog({ open, onClose, masterStores }) {
  const [f, setF] = useState({ nome: "", tipo: "proprio", data_inizio_dati: "", aliquota: "22", store_master_id: "" });
  const inp = "border border-slate-300 rounded-md px-2 py-1.5 text-sm w-full";
  const save = async () => {
    try {
      const res = await api.post("/stores", {
        nome: f.nome, tipo: f.tipo,
        data_inizio_dati: f.data_inizio_dati || null,
        aliquota_iva: (parseFloat(String(f.aliquota).replace(",", ".")) || 0) / 100,
        store_master_id: f.tipo === "figlio" ? (f.store_master_id || null) : null,
      });
      if (res.data?.avviso) toast.warning(res.data.avviso, { duration: 10000 });
      toast.success("Store creato");
      window.location.reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="nuovo-store-dialog">
        <DialogHeader><DialogTitle>Nuovo store</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-slate-500">Nome<input data-testid="nuovo-store-nome" className={inp} value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Tipo
            <select data-testid="nuovo-store-tipo" className={inp} value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })}>
              <option value="proprio">Proprio</option>
              <option value="figlio">Figlio</option>
              <option value="master">Master</option>
            </select></label>
          <label className="text-xs text-slate-500">Data inizio dati<input data-testid="nuovo-store-data-inizio" type="date" className={inp} value={f.data_inizio_dati} onChange={(e) => setF({ ...f, data_inizio_dati: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Aliquota IVA (%)<input data-testid="nuovo-store-aliquota" type="number" step="0.1" className={inp} value={f.aliquota} onChange={(e) => setF({ ...f, aliquota: e.target.value })} /></label>
          {f.tipo === "figlio" && (
            <label className="text-xs text-slate-500 col-span-2">Store master di riferimento
              <select data-testid="nuovo-store-master" className={inp} value={f.store_master_id} onChange={(e) => setF({ ...f, store_master_id: e.target.value })}>
                <option value="">—</option>
                {masterStores.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select></label>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-2">Costi spedizione e commissioni partono dai default (addebitati 8/6/0/16/14 € + giacenza 2 €, gateway 1,2%); costi reali e regola di ricarico si impostano dopo il salvataggio. Le credenziali API si impostano nella scheda dello store.</p>
        <button data-testid="nuovo-store-save" onClick={save} disabled={!f.nome}
          className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md px-4 py-2 w-full disabled:opacity-40">Crea store</button>
      </DialogContent>
    </Dialog>
  );
}

export default function ConfigurazionePage() {
  const { stores } = useOutletContext();
  const [sel, setSel] = useState(stores[0]?.id || "");
  const [store, setStore] = useState(null);
  const [showNewStore, setShowNewStore] = useState(false);
  const fileRef = useRef(null);
  const importConfig = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const res = await api.post("/configurazione/import", fd);
      toast.success(`Importazione completata: ${res.data.creati.length} creati, ${res.data.aggiornati.length} aggiornati`);
      if (res.data.errori?.length) toast.warning(`Errori su ${res.data.errori.length} store: ${res.data.errori[0].motivo}`);
      loadAllStores();
      if (store) api.get(`/stores/${store.id}`).then((r) => {
        const d = r.data;
        d.parametri = (d.parametri || []).map((p) => ({ ...p, costi_spedizione: normCosti(p.costi_spedizione) }));
        setStore(d);
      });
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const [cred, setCred] = useState({ shop_domain: "", api_version: "", client_id: "", client_secret: "", sp_endpoint: "", sp_chiave: "", meta_ids: "" });
  const [esiti, setEsiti] = useState({});
  const [globali, setGlobali] = useState(null);
  const [glob, setGlob] = useState({ sp_endpoint: "", sp_chiave: "", meta_token: "", hub_url: "", hub_chiave: "" });
  const [esitiGlob, setEsitiGlob] = useState({});
  const [users, setUsers] = useState([]);
  const [allStores, setAllStores] = useState([]);
  const loadAllStores = () => api.get("/stores").then((r) => setAllStores(r.data)).catch(() => {});
  const loadUsers = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { loadUsers(); }, []);
  useEffect(() => { loadAllStores(); }, []);
  const [nuovoUtente, setNuovoUtente] = useState({ name: "", email: "", role: "socio", store_id: "" });
  const [passwordMonouso, setPasswordMonouso] = useState(null);
  const [filtroUtenti, setFiltroUtenti] = useState("attivi");
  const [cercaUtenti, setCercaUtenti] = useState("");
  const storeNome = (id) => allStores.find((s) => s.id === id)?.nome || "—";
  const fmtDataOra = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "mai");
  const creaUtente = async () => {
    try {
      const res = await api.post("/users", {
        name: nuovoUtente.name, email: nuovoUtente.email, role: nuovoUtente.role,
        store_id: nuovoUtente.role === "socio" ? (nuovoUtente.store_id || null) : null,
      });
      setPasswordMonouso({ titolo: `Utente creato: ${res.data.email}`, password: res.data.password_iniziale });
      setNuovoUtente({ name: "", email: "", role: "socio", store_id: "" });
      loadUsers();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resetUtente = async (u) => {
    try {
      const res = await api.post(`/users/${u.id}/reset-password`);
      setPasswordMonouso({ titolo: `Nuova password per ${u.email}`, password: res.data.password_nuova });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const patchUtente = async (u, body, msg) => {
    try {
      await api.patch(`/users/${u.id}`, body);
      if (msg) toast.success(msg);
      loadUsers();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadGlobali = () => api.get("/impostazioni/integrazioni").then((r) => {
    setGlobali(r.data);
    setGlob({
      sp_endpoint: r.data.spediamo?.endpoint || "", sp_chiave: "", meta_token: "",
      hub_url: r.data.spedizioni_hub?.base_url || "https://spedizioni.mybrandonline.it", hub_chiave: "",
    });
  }).catch(() => {});
  useEffect(() => { loadGlobali(); }, []);
  const [ricostr, setRicostr] = useState(null);
  useEffect(() => { api.get("/impostazioni/ricostruzione-costo").then((r) => setRicostr(r.data)).catch(() => {}); }, []);
  const saveRicostr = async () => {
    try {
      const res = await api.put("/impostazioni/ricostruzione-costo", ricostr);
      setRicostr(res.data);
      toast.success("Regola di ricostruzione salvata: applicata ai calcoli da subito (nessuna scrittura su Shopify)");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { if (!sel && stores[0]) setSel(stores[0].id); }, [stores]);
  useEffect(() => {
    if (sel) {
      api.get(`/stores/${sel}`).then((r) => {
        const d = r.data;
        d.parametri = (d.parametri || []).map((p) => ({ ...p, costi_spedizione: normCosti(p.costi_spedizione) }));
        setStore(d);
        const cv = r.data.credenziali_view || {};
        setCred({
          shop_domain: cv.shopify?.shop_domain || "", api_version: cv.shopify?.api_version || "2026-07",
          client_id: "", client_secret: "",
          sp_endpoint: cv.spediamo?.endpoint || "", sp_chiave: "",
          meta_ids: (cv.meta?.ad_account_ids || []).join(", "),
        });
        setEsiti({});
      }).catch((e) => toast.error(formatApiError(e)));
    }
  }, [sel]);

  const saveGlobali = async () => {
    try {
      const payload = { spediamo: {}, meta: {}, spedizioni_hub: {} };
      if (glob.sp_endpoint) payload.spediamo.endpoint = glob.sp_endpoint;
      if (glob.sp_chiave) payload.spediamo.chiave = glob.sp_chiave;
      if (glob.meta_token) payload.meta.access_token = glob.meta_token;
      if (glob.hub_url) payload.spedizioni_hub.base_url = glob.hub_url;
      if (glob.hub_chiave) payload.spedizioni_hub.chiave = glob.hub_chiave;
      const res = await api.put("/impostazioni/integrazioni", payload);
      setGlobali(res.data);
      setGlob((g) => ({ ...g, sp_chiave: "", meta_token: "", hub_chiave: "" }));
      toast.success("Integrazioni globali salvate (cifrate)");
      loadAllStores();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const verificaGlob = async (servizio) => {
    try {
      const res = await api.post("/impostazioni/verifica-connessione", { servizio });
      setEsitiGlob((e) => ({ ...e, [servizio]: res.data }));
      await loadGlobali();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rimuoviOverrideSpediamo = async () => {
    try {
      const res = await api.put(`/stores/${store.id}/credenziali`, { spediamo: { rimuovi: true } });
      setStore(res.data);
      setCred((c) => ({ ...c, sp_endpoint: "", sp_chiave: "" }));
      toast.success("Credenziali dedicate rimosse: lo store torna all'account aziendale");
      loadAllStores();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [syncLog, setSyncLog] = useState([]);
  const [syncPeriodo, setSyncPeriodo] = useState({ da: "", a: "" });
  const [syncBusy, setSyncBusy] = useState(false);
  const loadSyncLog = () => api.get("/sync/log").then((r) => setSyncLog(r.data)).catch(() => {});
  useEffect(() => { loadSyncLog(); }, []);
  const syncInCorsoStore = syncLog.some((l) => l.stato === "in_corso" && l.store_id === store?.id);
  useEffect(() => {
    if (!syncLog.some((l) => l.stato === "in_corso")) return;
    const t = setInterval(loadSyncLog, 5000);
    return () => clearInterval(t);
  }, [syncLog]);

  const avviaSync = async () => {
    setSyncBusy(true);
    try {
      await api.post(`/stores/${store.id}/sync/shopify`, { da: syncPeriodo.da || null, a: syncPeriodo.a || null });
      toast.success("Sincronizzazione Shopify avviata: il registro in fondo si aggiorna da solo ogni 5 secondi");
      await loadSyncLog();
    } catch (e) { toast.error(formatApiError(e)); }
    setSyncBusy(false);
  };

  const [hubSyncBusy, setHubSyncBusy] = useState(false);
  const hubSyncInCorso = syncLog.some((l) => l.stato === "in_corso" && l.tipo === "spedizioni-hub");
  const avviaSyncHub = async () => {
    setHubSyncBusy(true);
    try {
      await api.post("/sync/spedizioni-hub");
      toast.success("Sincronizzazione Spedizioni Hub avviata: il registro in fondo si aggiorna da solo");
      await loadSyncLog();
    } catch (e) { toast.error(formatApiError(e)); }
    setHubSyncBusy(false);
  };

  const saveCred = async () => {
    try {
      const payload = { shopify: {}, spediamo: {}, meta: {} };
      if (cred.shop_domain) payload.shopify.shop_domain = cred.shop_domain;
      if (cred.api_version) payload.shopify.api_version = cred.api_version;
      if (cred.client_id) payload.shopify.client_id = cred.client_id;
      if (cred.client_secret) payload.shopify.client_secret = cred.client_secret;
      if (cred.sp_endpoint) payload.spediamo.endpoint = cred.sp_endpoint;
      if (cred.sp_chiave) payload.spediamo.chiave = cred.sp_chiave;
      payload.meta.ad_account_ids = cred.meta_ids;
      const res = await api.put(`/stores/${store.id}/credenziali`, payload);
      setStore(res.data);
      setCred((c) => ({ ...c, client_id: "", client_secret: "", sp_chiave: "" }));
      toast.success("Credenziali salvate (cifrate)");
      loadAllStores();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const verifica = async (servizio) => {
    try {
      const res = await api.post(`/stores/${store.id}/verifica-connessione`, { servizio });
      setEsiti((e) => ({ ...e, [servizio]: res.data }));
      const fresh = await api.get(`/stores/${store.id}`);
      setStore(fresh.data);
      loadAllStores();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!store) return <div className="text-sm text-slate-500">Caricamento configurazione…</div>;

  const inp = "border border-slate-300 rounded-md px-2 py-1.5 text-sm w-full";
  const setP = (i, k, v) => setStore((s) => ({ ...s, parametri: s.parametri.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));
  const setPCosto = (i, k, campo, v) => setStore((s) => ({
    ...s,
    parametri: s.parametri.map((p, j) => {
      if (j !== i) return p;
      const voce = { ...(p.costi_spedizione?.[k] || { addebitato: 0, reale: 0 }), [campo]: v };
      // regola di ricarico attiva: l'addebitato segue il reale, ma resta sovrascrivibile a mano
      if (campo === "reale") {
        const sug = applicaRicarico(v, s.regola_ricarico_spedizioni);
        if (sug != null) voce.addebitato = sug;
      }
      return { ...p, costi_spedizione: { ...p.costi_spedizione, [k]: voce } };
    }),
  }));
  const setPCommFam = (i, k, v) => setStore((s) => ({ ...s, parametri: s.parametri.map((p, j) => (j === i ? { ...p, commissioni_famiglia: { ...p.commissioni_famiglia, [k]: v } } : p)) }));
  const setPCommDet = (i, k, v) => setStore((s) => ({ ...s, parametri: s.parametri.map((p, j) => (j === i ? { ...p, commissioni_dettaglio: { ...p.commissioni_dettaglio, [k]: v } } : p)) }));

  const save = async () => {
    try {
      const res = await api.put(`/stores/${store.id}`, {
        data_inizio_dati: store.data_inizio_dati,
        valuta: store.valuta,
        regola_quote: store.regola_quote,
        parametri: store.parametri,
        ripartizione: { ...rip, da_definire: rip.numero_quote_totali > 0 && somma !== rip.numero_quote_totali },
        regola_ricarico_spedizioni: store.regola_ricarico_spedizioni || { tipo: "manuale", valore: 0 },
        ...(store.tasso_incasso_cod != null ? { tasso_incasso_cod: store.tasso_incasso_cod } : {}),
      });
      if (res.data?.avviso) toast.warning(res.data.avviso, { duration: 10000 });
      toast.success(`Parametri salvati per ${store.nome}. Gli ordini già creati mantengono i valori congelati: usa 'Ricalcola' sulla griglia ordini per applicare i nuovi parametri.`);
      // Avviso listini disallineati: il salvataggio non si propaga agli altri store
      const altri = await Promise.all(
        stores.filter((s) => s.id !== store.id)
          .map((s) => api.get(`/stores/${s.id}`).then((r) => r.data).catch(() => null)));
      const mio = JSON.stringify((store.parametri || []).map((p) => p.costi_spedizione));
      const diversi = altri.filter(Boolean)
        .filter((s) => JSON.stringify((s.parametri || []).map((p) => p.costi_spedizione)) !== mio)
        .map((s) => s.nome);
      if (diversi.length) {
        toast.warning(`Listino spedizioni diverso su: ${diversi.join(", ")} — il salvataggio è valso solo per ${store.nome}.`, { duration: 9000 });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const num = (v) => (v == null ? "" : v);
  const sogliaApi = new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 7);
  const rip = store.ripartizione || { compenso_fisso_gestione: 0, numero_quote_totali: 0, partecipanti: [] };
  const setRip = (r) => setStore((s) => ({ ...s, ripartizione: r }));
  const setPart = (i, k, v) => setRip({ ...rip, partecipanti: rip.partecipanti.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  const somma = (rip.partecipanti || []).reduce((a, p) => a + (parseInt(p.quote) || 0), 0);
  const nUtentiAttivi = users.filter((u) => u.attivo !== false).length;
  const utentiVisibili = users.filter((u) => {
    if (filtroUtenti === "attivi" && u.attivo === false) return false;
    if (filtroUtenti === "disattivati" && u.attivo !== false) return false;
    const q = cercaUtenti.trim().toLowerCase();
    return !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  return (
    <div data-testid="configurazione-page" className="max-w-5xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Configurazione store</h1>
        <select data-testid="config-store-select" className={inp + " w-56"} value={sel} onChange={(e) => setSel(e.target.value)}>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.nome} ({s.tipo})</option>)}
        </select>
        <button data-testid="config-export-btn"
          onClick={() => { window.location.href = `${api.defaults.baseURL}/configurazione/export`; }}
          className="text-sm font-semibold border border-slate-300 rounded-md px-3 py-1.5 hover:bg-slate-50 transition-colors">Esporta configurazione</button>
        <input ref={fileRef} type="file" accept="application/json" className="hidden" data-testid="config-import-file" onChange={importConfig} />
        <button data-testid="config-import-btn" onClick={() => fileRef.current.click()}
          className="text-sm font-semibold border border-slate-300 rounded-md px-3 py-1.5 hover:bg-slate-50 transition-colors">Importa configurazione</button>
        <button data-testid="nuovo-store-button" onClick={() => setShowNewStore(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md px-3 py-1.5 transition-colors">+ Nuovo store</button>
      </div>

      <div data-testid="config-store-banner" className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2.5 flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-indigo-500 uppercase tracking-wider font-semibold">Stai lavorando su</span>
        <span data-testid="config-store-banner-nome" className="text-base font-bold text-indigo-900">{store.nome}</span>
        <span className="text-xs text-indigo-400">({store.tipo})</span>
        <span className="text-[11px] text-indigo-400 ml-auto">Ogni modifica in questa pagina vale SOLO per questo store</span>
      </div>

      <div className="card p-5 space-y-5" data-testid="integrazioni-globali-card">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Integrazioni globali (azienda)</h2>
          <p className="text-xs text-slate-400 mt-1">
            Spediamo Pro e Meta Ads sono credenziali aziendali, valide per tutti gli store: si configurano una volta sola qui.
            Shopify resta per store (sezione credenziali del singolo store, più sotto).
          </p>
        </div>
        {globali?.nota_migrazione && (
          <div data-testid="nota-migrazione-spediamo" className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 text-xs">{globali.nota_migrazione}</div>
        )}
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Spediamo Pro — account aziendale</h3>
              <StatoPill st={globali?.spediamo?.stato} testid="stato-global-spediamo" />
            </div>
            <div className="flex items-center gap-2">
              {esitiGlob.spediamo && <span data-testid="verifica-esito-global-spediamo" className={`text-xs rounded px-2 py-1 ${esitiGlob.spediamo.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>{esitiGlob.spediamo.messaggio}</span>}
              <button data-testid="verifica-global-spediamo" onClick={() => verificaGlob("spediamo")} className="text-xs font-semibold border border-slate-300 rounded px-2 py-1 hover:bg-slate-50">Verifica connessione</button>
            </div>
          </div>
          <StatoDettaglio st={globali?.spediamo?.stato} testid="stato-dettaglio-global-spediamo" />
          <div className="grid grid-cols-2 gap-3 mt-1">
            <label className="text-xs text-slate-500">Endpoint<input data-testid="global-sp-endpoint" className={inp} placeholder="https://api.spediamopro.it/..." value={glob.sp_endpoint} onChange={(e) => setGlob({ ...glob, sp_endpoint: e.target.value })} /></label>
            <label className="text-xs text-slate-500">Chiave API<input data-testid="global-sp-chiave" type="password" className={inp} placeholder={globali?.spediamo?.chiave ? `${globali.spediamo.chiave} (invariata se vuoto)` : ""} value={glob.sp_chiave} onChange={(e) => setGlob({ ...glob, sp_chiave: e.target.value })} /></label>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Meta Ads — account aziendale</h3>
              <StatoPill st={globali?.meta?.stato} testid="stato-global-meta" />
            </div>
            <div className="flex items-center gap-2">
              {esitiGlob.meta && <span data-testid="verifica-esito-global-meta" className={`text-xs rounded px-2 py-1 ${esitiGlob.meta.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>{esitiGlob.meta.messaggio}</span>}
              <button data-testid="verifica-global-meta" onClick={() => verificaGlob("meta")} className="text-xs font-semibold border border-slate-300 rounded px-2 py-1 hover:bg-slate-50">Verifica connessione</button>
            </div>
          </div>
          <StatoDettaglio st={globali?.meta?.stato} testid="stato-dettaglio-global-meta" />
          <label className="text-xs text-slate-500">Access token API<input data-testid="global-meta-token" type="password" className={inp} placeholder={globali?.meta?.access_token ? `${globali.meta.access_token} (invariato se vuoto)` : ""} value={glob.meta_token} onChange={(e) => setGlob({ ...glob, meta_token: e.target.value })} /></label>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Spedizioni Hub — costi reali e stati consegna</h3>
              <StatoPill st={globali?.spedizioni_hub?.stato} testid="stato-global-hub" />
            </div>
            <div className="flex items-center gap-2">
              {esitiGlob.spedizioni_hub && <span data-testid="verifica-esito-global-hub" className={`text-xs rounded px-2 py-1 ${esitiGlob.spedizioni_hub.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>{esitiGlob.spedizioni_hub.messaggio}</span>}
              <button data-testid="verifica-global-hub" onClick={() => verificaGlob("spedizioni_hub")} className="text-xs font-semibold border border-slate-300 rounded px-2 py-1 hover:bg-slate-50">Verifica connessione</button>
            </div>
          </div>
          <StatoDettaglio st={globali?.spedizioni_hub?.stato} testid="stato-dettaglio-global-hub" />
          <div className="grid grid-cols-2 gap-3 mt-1">
            <label className="text-xs text-slate-500">URL base<input data-testid="global-hub-url" className={inp} value={glob.hub_url} onChange={(e) => setGlob({ ...glob, hub_url: e.target.value })} /></label>
            <label className="text-xs text-slate-500">Chiave API (inviata nell'header X-API-Key)<input data-testid="global-hub-chiave" type="password" className={inp} placeholder={globali?.spedizioni_hub?.chiave ? `${globali.spedizioni_hub.chiave} (invariata se vuoto)` : "shk_…"} value={glob.hub_chiave} onChange={(e) => setGlob({ ...glob, hub_chiave: e.target.value })} /></label>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Accesso di sola lettura. Finché la chiave non è inserita l'integrazione resta "non configurata" e la sincronizzazione delle spedizioni non parte.</p>
          <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="sync-hub-section">
            <button data-testid="sync-hub-btn" onClick={avviaSyncHub} disabled={hubSyncBusy || hubSyncInCorso}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-md px-3 py-2 disabled:opacity-40 transition-colors">
              {hubSyncBusy || hubSyncInCorso ? "Sincronizzazione in corso…" : "Sincronizza costi e consegne da Hub"}
            </button>
            <span className="text-[11px] text-slate-400 max-w-xl">
              Aggiorna costo reale (quando l'hub lo dà per finale), stato e data di consegna. Automatica ogni 4 ore.
              Il costo hub finale è già il totale della spedizione: sostituisce la stima senza sommare gli eventi.
              I valori corretti a mano non vengono mai sovrascritti: le differenze finiscono in Divergenze.
            </span>
          </div>
        </div>
        <button data-testid="globali-save-btn" onClick={saveGlobali}
          className="bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-md px-4 py-2 transition-colors">Salva integrazioni globali</button>
      </div>

      <div className="card p-5 space-y-4" data-testid="ricostruzione-costo-card">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Costo reale master — ricostruzione da collezioni</h2>
          <p className="text-xs text-slate-400 mt-1 max-w-3xl">
            Il tool di importazione dello store master scrive nel costo lo stesso valore del prezzo. Quando costo = prezzo, il costo reale
            viene ricalcolato qui dentro (mai scritto su Shopify) come prezzo meno il ricarico fisso della collezione. Se costo e prezzo
            differiscono, il costo letto resta intatto. I valori ricostruiti sono marcati "≈ ricostr." e il ricarico che ne deriva è stimato.
          </p>
        </div>
        {ricostr && (
          <>
            {ricostr.regole.map((r, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-[180px_1fr_120px_40px] gap-2 items-center">
                <input data-testid={`ricostr-etichetta-${i}`} className={inp} placeholder="Etichetta" value={r.etichetta}
                  onChange={(e) => setRicostr((c) => ({ ...c, regole: c.regole.map((x, j) => j === i ? { ...x, etichetta: e.target.value } : x) }))} />
                <input data-testid={`ricostr-collezioni-${i}`} className={inp} placeholder="Collezioni (virgola): es. Abiti eleganti, Abiti casual"
                  value={(r.collezioni || []).join(", ")}
                  onChange={(e) => setRicostr((c) => ({ ...c, regole: c.regole.map((x, j) => j === i ? { ...x, collezioni: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } : x) }))} />
                <input data-testid={`ricostr-detrazione-${i}`} type="number" step="0.01" className={inp} value={r.detrazione}
                  onChange={(e) => setRicostr((c) => ({ ...c, regole: c.regole.map((x, j) => j === i ? { ...x, detrazione: parseFloat(e.target.value) || 0 } : x) }))} />
                <button data-testid={`ricostr-rm-${i}`} className="text-slate-400 hover:text-red-600 font-bold text-lg"
                  onClick={() => setRicostr((c) => ({ ...c, regole: c.regole.filter((_, j) => j !== i) }))}>×</button>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-3">
              <button data-testid="ricostr-add" onClick={() => setRicostr((c) => ({ ...c, regole: [...c.regole, { etichetta: "", collezioni: [], detrazione: 5.0 }] }))}
                className="text-xs font-semibold border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50">+ Aggiungi regola</button>
              <label className="text-xs text-slate-500 flex items-center gap-2">Detrazione predefinita (altre collezioni) €
                <input data-testid="ricostr-default" type="number" step="0.01" className={inp + " w-24"} value={ricostr.default}
                  onChange={(e) => setRicostr((c) => ({ ...c, default: parseFloat(e.target.value) || 0 }))} /></label>
              <button data-testid="ricostr-save" onClick={saveRicostr}
                className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded px-4 py-1.5">Salva regola</button>
            </div>
            <p className="text-[11px] text-slate-400">Se più regole sono applicabili allo stesso prodotto vale l'importo più alto (segnalato). Se prezzo meno importo fosse zero o negativo, la regola non si applica e il prodotto finisce in Dati mancanti.</p>
          </>
        )}
      </div>

      <div className="card p-5 space-y-4" data-testid="utenti-card">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Gestione utenti</h2>
          <p className="text-xs text-slate-400 mt-1 max-w-3xl">
            Sezione riservata al master. Le password sono generate dal sistema e mostrate <strong>una sola volta</strong>:
            copiale e consegnale all'interessato, che potrà poi cambiarla dal menu in basso a sinistra ("Cambia password").
            Gli utenti non si eliminano mai: la disattivazione blocca l'accesso ma conserva l'identità nel log delle modifiche.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="utenti-search" className={inp + " w-64"} placeholder="Cerca per nome o email…"
            value={cercaUtenti} onChange={(e) => setCercaUtenti(e.target.value)} />
          <div className="flex rounded-md border border-slate-300 overflow-hidden" data-testid="utenti-filtro-stato">
            {[["attivi", "Attivi"], ["disattivati", "Disattivati"], ["tutti", "Tutti"]].map(([v, l]) => (
              <button key={v} data-testid={`utenti-filtro-${v}`} onClick={() => setFiltroUtenti(v)}
                className={`text-xs font-semibold px-3 py-2 transition-colors ${filtroUtenti === v ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          <span data-testid="utenti-count" className="text-xs text-slate-400 ml-auto whitespace-nowrap">
            {nUtentiAttivi} attivi · {users.length - nUtentiAttivi} disattivati
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-slate-100">
                {["Nome", "Email", "Ruolo", "Store", "Stato", "Ultimo accesso", "Creato il", "Azioni"].map((h) => (
                  <th key={h} className="py-1.5 pr-3 text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {utentiVisibili.map((u) => (
                <tr key={u.id} data-testid={`utente-row-${u.email}`} className="border-b border-slate-50">
                  <td className="py-2 pr-3 font-medium text-slate-700">{u.name || "—"}</td>
                  <td className="py-2 pr-3 text-xs">{u.email}</td>
                  <td className="py-2 pr-3">
                    <select data-testid={`utente-ruolo-${u.email}`} className="text-xs border border-slate-300 rounded px-1.5 py-1"
                      value={u.role}
                      onChange={(e) => patchUtente(u, { role: e.target.value, store_id: e.target.value === "socio" ? (u.store_id || allStores.find((s) => s.tipo !== "master")?.id || null) : null }, `Ruolo di ${u.email} aggiornato`)}>
                      <option value="master">Master</option>
                      <option value="socio">Socio</option>
                    </select>
                  </td>
                  <td className="py-2 pr-3">
                    {u.role === "socio" ? (
                      <select data-testid={`utente-store-${u.email}`} className="text-xs border border-slate-300 rounded px-1.5 py-1"
                        value={u.store_id || ""}
                        onChange={(e) => patchUtente(u, { store_id: e.target.value }, `Store di ${u.email} aggiornato`)}>
                        <option value="">— scegli —</option>
                        {allStores.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                      </select>
                    ) : <span className="text-xs text-slate-400">tutti</span>}
                  </td>
                  <td className="py-2 pr-3">
                    <span data-testid={`utente-stato-${u.email}`} className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${u.attivo !== false ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {u.attivo !== false ? "Attivo" : "Disattivato"}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-xs text-slate-500 whitespace-nowrap" data-testid={`utente-accesso-${u.email}`}>{fmtDataOra(u.ultimo_accesso)}</td>
                  <td className="py-2 pr-3 text-xs text-slate-500 whitespace-nowrap">{fmtDataOra(u.created_at)}</td>
                  <td className="py-2 whitespace-nowrap">
                    <button data-testid={`utente-reset-${u.email}`} onClick={() => resetUtente(u)}
                      className="text-xs font-semibold border border-slate-300 rounded px-2 py-1 hover:bg-slate-50 mr-1">Reimposta password</button>
                    <button data-testid={`utente-toggle-${u.email}`}
                      onClick={() => patchUtente(u, { attivo: u.attivo === false }, u.attivo === false ? `${u.email} riattivato` : `${u.email} disattivato`)}
                      className={`text-xs font-semibold rounded px-2 py-1 border ${u.attivo !== false ? "text-red-600 border-red-200 hover:bg-red-50" : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"}`}>
                      {u.attivo !== false ? "Disattiva" : "Riattiva"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100 pt-4" data-testid="nuovo-utente-form">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Nuovo utente</h3>
          <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_130px_1fr_auto] gap-2 items-end">
            <label className="text-xs text-slate-500">Nome
              <input data-testid="nuovo-utente-nome" className={inp} value={nuovoUtente.name}
                onChange={(e) => setNuovoUtente({ ...nuovoUtente, name: e.target.value })} /></label>
            <label className="text-xs text-slate-500">Email
              <input data-testid="nuovo-utente-email" type="email" className={inp} value={nuovoUtente.email}
                onChange={(e) => setNuovoUtente({ ...nuovoUtente, email: e.target.value })} /></label>
            <label className="text-xs text-slate-500">Ruolo
              <select data-testid="nuovo-utente-ruolo" className={inp} value={nuovoUtente.role}
                onChange={(e) => setNuovoUtente({ ...nuovoUtente, role: e.target.value })}>
                <option value="socio">Socio</option>
                <option value="master">Master</option>
              </select></label>
            {nuovoUtente.role === "socio" ? (
              <label className="text-xs text-slate-500">Store di competenza (obbligatorio)
                <select data-testid="nuovo-utente-store" className={inp} value={nuovoUtente.store_id}
                  onChange={(e) => setNuovoUtente({ ...nuovoUtente, store_id: e.target.value })}>
                  <option value="">— scegli —</option>
                  {allStores.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select></label>
            ) : <span className="text-[11px] text-slate-400 pb-2">Il master vede tutti gli store</span>}
            <button data-testid="nuovo-utente-crea" onClick={creaUtente}
              disabled={!nuovoUtente.name.trim() || !nuovoUtente.email.trim() || (nuovoUtente.role === "socio" && !nuovoUtente.store_id)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-40 transition-colors">Crea utente</button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">La password iniziale viene generata dal sistema e mostrata una sola volta subito dopo la creazione.</p>
        </div>
        <Dialog open={!!passwordMonouso} onOpenChange={() => setPasswordMonouso(null)}>
          <DialogContent data-testid="password-monouso-dialog">
            <DialogHeader><DialogTitle>{passwordMonouso?.titolo}</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500">Password generata dal sistema, visibile <strong>solo adesso</strong>: copiala e consegnala all'interessato. Non sarà più recuperabile (potrai sempre generarne una nuova con "Reimposta password").</p>
            <div className="flex items-center gap-2 mt-2">
              <code data-testid="password-monouso-valore" className="flex-1 font-mono text-sm bg-slate-100 border border-slate-200 rounded px-3 py-2 select-all">{passwordMonouso?.password}</code>
              <button data-testid="password-monouso-copia"
                onClick={() => { navigator.clipboard?.writeText(passwordMonouso?.password || ""); toast.success("Password copiata"); }}
                className="text-xs font-semibold border border-slate-300 rounded px-3 py-2 hover:bg-slate-50">Copia</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card p-5" data-testid="integrazioni-riepilogo">
        <h2 className="text-sm font-semibold text-slate-800 mb-3">Riepilogo integrazioni</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-slate-100">
                <th className="py-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">Ambito</th>
                <th className="text-xs font-bold uppercase tracking-wider text-slate-500">Shopify</th>
                <th className="text-xs font-bold uppercase tracking-wider text-slate-500">Spediamo Pro</th>
                <th className="text-xs font-bold uppercase tracking-wider text-slate-500">Meta Ads</th>
                <th className="text-xs font-bold uppercase tracking-wider text-slate-500">Spedizioni Hub</th>
              </tr>
            </thead>
            <tbody>
              <tr data-testid="integrazioni-row-azienda" className="border-b border-slate-200 bg-slate-50/60">
                <td className="py-2 font-semibold text-slate-800 pr-4">Azienda (globale)</td>
                <td className="py-2 pr-2 text-xs text-slate-400">per store</td>
                <td className="py-2 pr-2"><StatoPill st={globali?.spediamo?.stato} testid="riepilogo-spediamo-globale" /></td>
                <td className="py-2 pr-2"><StatoPill st={globali?.meta?.stato} testid="riepilogo-meta-globale" /></td>
                <td className="py-2 pr-2"><StatoPill st={globali?.spedizioni_hub?.stato} testid="riepilogo-hub-globale" /></td>
              </tr>
              {allStores.map((s) => (
                <tr key={s.id} data-testid={`integrazioni-row-${s.nome}`} className="border-b border-slate-50">
                  <td className="py-2 font-medium text-slate-700 pr-4">{s.nome}</td>
                  <td className="py-2 pr-2"><StatoPill st={s.credenziali_view?.shopify?.stato} testid={`riepilogo-shopify-${s.nome}`} /></td>
                  <td className="py-2 pr-2 text-xs" data-testid={`riepilogo-spediamo-${s.nome}`}>
                    {s.credenziali_view?.spediamo?.override
                      ? <span className="bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 font-semibold">Dedicate</span>
                      : <span className="text-slate-400">Globale</span>}
                  </td>
                  <td className="py-2 pr-2 text-xs text-slate-600" data-testid={`riepilogo-meta-${s.nome}`}>
                    {(s.credenziali_view?.meta?.ad_account_ids || []).length} account collegati
                  </td>
                  <td className="py-2 pr-2 text-xs text-slate-400" data-testid={`riepilogo-hub-${s.nome}`}>Globale</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <label className="text-xs text-slate-500">Data inizio dati
            <input data-testid="config-data-inizio" type="date" className={inp} value={store.data_inizio_dati || ""}
              onChange={(e) => setStore({ ...store, data_inizio_dati: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Valuta
            <input className={inp} value={store.valuta} onChange={(e) => setStore({ ...store, valuta: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Store master (per figli)
            <select className={inp} value={store.store_master_id || ""}
              onChange={(e) => setStore({ ...store, store_master_id: e.target.value || null })}>
              <option value="">—</option>
              {stores.filter((s) => s.tipo === "master").map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select></label>
          <label className="text-xs text-slate-500">Tasso incasso COD manuale (% — usato se storico insufficiente)
            <input data-testid="config-tasso-cod" type="number" step="0.1" className={inp}
              value={store.tasso_incasso_cod != null ? (store.tasso_incasso_cod * 100) : ""} placeholder="85"
              onChange={(e) => setStore({ ...store, tasso_incasso_cod: e.target.value === "" ? null : (parseFloat(e.target.value) || 0) / 100 })} /></label>
          <label className="text-xs text-slate-500">Regola di ricarico spedizioni
            <select data-testid="config-regola-ricarico" className={inp}
              value={store.regola_ricarico_spedizioni?.tipo || "manuale"}
              onChange={(e) => setStore({ ...store, regola_ricarico_spedizioni: { valore: 0, ...(store.regola_ricarico_spedizioni || {}), tipo: e.target.value } })}>
              <option value="manuale">Manuale — addebitato e reale indipendenti</option>
              <option value="fisso">Maggiorazione fissa — addebitato = reale + X €</option>
              <option value="percentuale">Maggiorazione percentuale — addebitato = reale + X%</option>
              <option value="arrotondamento">Arrotondamento per eccesso — al multiplo di X €</option>
            </select></label>
          {(store.regola_ricarico_spedizioni?.tipo || "manuale") !== "manuale" && (
            <label className="text-xs text-slate-500">Valore X della regola di ricarico
              <input data-testid="config-regola-ricarico-valore" type="number" step="0.01" className={inp}
                value={store.regola_ricarico_spedizioni?.valore ?? ""}
                onChange={(e) => setStore({ ...store, regola_ricarico_spedizioni: { ...store.regola_ricarico_spedizioni, valore: parseFloat(e.target.value) || 0 } })} /></label>
          )}
          {(store.regola_ricarico_spedizioni?.tipo || "manuale") !== "manuale" && (
            <p className="text-[11px] text-slate-400 col-span-3">
              Con la regola attiva, l'addebitato si ricalcola automaticamente al variare del costo reale, ma resta sempre modificabile a mano: in quel caso il valore manuale è quello ufficiale.
            </p>
          )}
        </div>

        <div className="border-t border-slate-100 pt-4" data-testid="ripartizione-section">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-sm font-semibold text-slate-800">Ripartizione utile</h2>
            {rip.da_definire && (
              <span data-testid="ripartizione-da-definire" className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">Da definire</span>
            )}
          </div>
          {rip.da_definire && (
            <div data-testid="ripartizione-incompleta-avviso" className="mb-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-xs">
              Ripartizione incompleta: assegna le quote ai partecipanti fino a raggiungere il totale. Finché resta incompleta, le quote soci non vengono calcolate.
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 mb-3">
            <label className="text-xs text-slate-500">Compenso fisso gestione (€/mese, sottratto prima della divisione)
              <input data-testid="config-rip-fisso" type="number" step="0.01" className={inp} value={rip.compenso_fisso_gestione ?? ""}
                onChange={(e) => setRip({ ...rip, compenso_fisso_gestione: parseFloat(e.target.value) || 0 })} /></label>
            <label className="text-xs text-slate-500">Numero quote totali (0 = nessuna ripartizione)
              <input data-testid="config-rip-totali" type="number" className={inp} value={rip.numero_quote_totali ?? 0}
                onChange={(e) => setRip({ ...rip, numero_quote_totali: parseInt(e.target.value) || 0 })} /></label>
          </div>
          {rip.numero_quote_totali > 0 && (
            <div className="space-y-2">
              {(rip.partecipanti || []).map((p, i) => (
                <div key={p.id || i} data-testid={`partecipante-row-${i}`} className="grid grid-cols-[1fr_90px_140px_1fr_32px] gap-2 items-center">
                  <input className={inp} placeholder="Nome" value={p.nome || ""} data-testid={`partecipante-nome-${i}`}
                    onChange={(e) => setPart(i, "nome", e.target.value)} />
                  <input className={inp} type="number" placeholder="Quote" value={p.quote ?? 0} data-testid={`partecipante-quote-${i}`}
                    onChange={(e) => setPart(i, "quote", parseInt(e.target.value) || 0)} />
                  <select className={inp} value={p.tipo || "socio"} data-testid={`partecipante-tipo-${i}`}
                    onChange={(e) => setPart(i, "tipo", e.target.value)}>
                    <option value="proprietario">Proprietario</option>
                    <option value="socio">Socio</option>
                  </select>
                  <select className={inp} value={p.user_id || ""} data-testid={`partecipante-user-${i}`}
                    onChange={(e) => setPart(i, "user_id", e.target.value || null)}>
                    <option value="">Utente collegato (opz.)</option>
                    {users.filter((u) => u.role === "socio").map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
                  </select>
                  <button data-testid={`partecipante-remove-${i}`} onClick={() => setRip({ ...rip, partecipanti: rip.partecipanti.filter((_, j) => j !== i) })}
                    className="text-slate-400 hover:text-red-600 text-lg leading-none">×</button>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <button data-testid="partecipante-add" onClick={() => setRip({ ...rip, partecipanti: [...(rip.partecipanti || []), { nome: "", quote: 0, tipo: "socio", user_id: null }] })}
                  className="text-sm text-indigo-600 hover:underline">+ Aggiungi partecipante</button>
                <span data-testid="quote-somma" className={`text-xs font-mono ${somma === rip.numero_quote_totali ? "text-emerald-700" : "text-red-600 font-bold"}`}>
                  {somma}/{rip.numero_quote_totali} quote assegnate
                  {somma !== rip.numero_quote_totali && (somma < rip.numero_quote_totali ? ` — mancano ${rip.numero_quote_totali - somma}` : ` — avanzano ${somma - rip.numero_quote_totali}`)}
                </span>
              </div>
            </div>
          )}
          {(!rip.numero_quote_totali || rip.numero_quote_totali === 0) && (
            <p className="text-xs text-slate-400">Nessuna ripartizione: l'utile resta interamente allo store.</p>
          )}
        </div>
      </div>

      {store.credenziali_view && (
        <div data-testid="credenziali-card" className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Credenziali integrazioni (preparazione FASE 2)</h2>
            <p className="text-xs text-slate-400 mt-1">Cifrate a riposo, mai restituite dalle API: vedi solo gli ultimi caratteri. Campo vuoto = invariato. La verifica connessione è simulata in questa fase.</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Shopify</h3>
                  <StatoPill st={store.credenziali_view.shopify.stato} testid="stato-shopify" />
                </div>
                <StatoDettaglio st={store.credenziali_view.shopify.stato} testid="stato-dettaglio-shopify" />
              </div>
              <div className="flex items-center gap-2">
                {esiti.shopify && <span data-testid="verifica-esito-shopify" className={`text-xs rounded px-2 py-1 ${esiti.shopify.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>{esiti.shopify.messaggio}</span>}
                <button data-testid="verifica-shopify" onClick={() => verifica("shopify")} className="text-xs font-semibold border border-slate-300 rounded px-2 py-1 hover:bg-slate-50">Verifica connessione</button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <label className="text-xs text-slate-500">Shop domain<input data-testid="cred-shop-domain" className={inp} placeholder="mio-shop.myshopify.com" value={cred.shop_domain} onChange={(e) => setCred({ ...cred, shop_domain: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Versione API (default 2026-07)<input data-testid="cred-api-version" className={inp} placeholder="2026-07" value={cred.api_version} onChange={(e) => setCred({ ...cred, api_version: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Client ID<input data-testid="cred-client-id" className={inp} placeholder={store.credenziali_view.shopify.client_id ? `${store.credenziali_view.shopify.client_id} (invariato se vuoto)` : ""} value={cred.client_id} onChange={(e) => setCred({ ...cred, client_id: e.target.value })} /></label>
              <label className="text-xs text-slate-500 col-span-3">Client secret<input data-testid="cred-client-secret" type="password" className={inp} placeholder={store.credenziali_view.shopify.client_secret ? `${store.credenziali_view.shopify.client_secret} (invariato se vuoto)` : ""} value={cred.client_secret} onChange={(e) => setCred({ ...cred, client_secret: e.target.value })} /></label>
            </div>
            {(store.credenziali_view.shopify.api_version_obsoleta || (cred.api_version && cred.api_version < sogliaApi)) && (
              <div data-testid="cred-api-version-warning" className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 mt-2">
                La versione API è più vecchia di un anno: Shopify dismette le versioni dopo circa 12 mesi. Aggiornala (es. 2026-07 o successiva).
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-2">
              Autenticazione OAuth 2.0 (client credentials grant): l'access token viene richiesto a runtime e rinnovato automaticamente, non va incollato nulla.
              Il flusso funziona solo se app e store appartengono alla <b>stessa organizzazione Shopify</b>: in caso di errore di autorizzazione, quella è la causa probabile.
            </p>
            <p className="text-[11px] text-slate-400 mt-1" data-testid="shopify-scope-note">
              Per collegare un nuovo store servono gli scope <b>read_orders</b> e <b>read_products</b> (più il permesso "View product costs" per i costi).
              <b> Non richiedere <code>read_all_orders</code></b> finché Shopify non lo approva separatamente: senza approvazione fa rifiutare l'intero gruppo di scope, e serve solo per leggere ordini più vecchi di 60 giorni.
            </p>
            <div className="mt-3 border-t border-slate-100 pt-3" data-testid="sync-shopify-section">
              <h4 className="text-xs font-semibold text-slate-600 mb-1">Sincronizzazione ordini</h4>
              <p className="text-[11px] text-slate-400 mb-2">
                Import reale da Shopify: idempotente (l'ordine si riconosce dall'id Shopify) e incrementale dall'ultima sync.
                Con il periodo valorizzato rilegge tutto il periodo senza duplicare. I valori corretti a mano non vengono mai sovrascritti: le differenze finiscono in Divergenze.
                Se la connessione cade, la sync riprende dall'ultima pagina salvata; il registro mostra l'avanzamento in tempo reale e marca come interrotte le esecuzioni senza progressi da oltre 30 minuti.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs text-slate-500">Da<input data-testid="sync-da" type="date" className={inp + " w-auto"} value={syncPeriodo.da} onChange={(e) => setSyncPeriodo({ ...syncPeriodo, da: e.target.value })} /></label>
                <label className="text-xs text-slate-500">A<input data-testid="sync-a" type="date" className={inp + " w-auto"} value={syncPeriodo.a} onChange={(e) => setSyncPeriodo({ ...syncPeriodo, a: e.target.value })} /></label>
                <button data-testid="sync-shopify-btn" onClick={avviaSync} disabled={syncBusy || syncInCorsoStore}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-md px-3 py-2 disabled:opacity-40 transition-colors">
                  {syncBusy || syncInCorsoStore ? "Sincronizzazione in corso…" : "Sincronizza ordini da Shopify"}
                </button>
                <span className="text-[11px] text-slate-400">Periodo vuoto = incrementale dall'ultimo aggiornamento noto</span>
              </div>
            </div>
          </div>
          <details data-testid="spediamo-override-section" className="border border-slate-200 rounded-md px-3 py-2">
            <summary className="text-xs font-semibold uppercase tracking-wider text-slate-500 cursor-pointer select-none">
              Avanzate — credenziali Spediamo Pro dedicate per questo store (facoltative)
              {store.credenziali_view.spediamo.override && <span data-testid="spediamo-override-attivo" className="ml-2 text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 normal-case">attive</span>}
            </summary>
            <p className="text-[11px] text-slate-400 mt-2">
              Di default questo store usa l'account aziendale (sezione Integrazioni globali in alto). Valorizza questi campi solo in caso di contratto corriere separato: le credenziali dedicate prevalgono su quelle globali.
            </p>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <label className="text-xs text-slate-500">Endpoint<input data-testid="cred-sp-endpoint" className={inp} placeholder="https://api.spediamopro.it/..." value={cred.sp_endpoint} onChange={(e) => setCred({ ...cred, sp_endpoint: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Chiave API<input data-testid="cred-sp-chiave" type="password" className={inp} placeholder={store.credenziali_view.spediamo.chiave ? `${store.credenziali_view.spediamo.chiave} (invariato se vuoto)` : ""} value={cred.sp_chiave} onChange={(e) => setCred({ ...cred, sp_chiave: e.target.value })} /></label>
            </div>
            <div className="flex items-center gap-2 mt-2">
              {esiti.spediamo && <span data-testid="verifica-esito-spediamo" className={`text-xs rounded px-2 py-1 ${esiti.spediamo.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>{esiti.spediamo.messaggio}</span>}
              <button data-testid="verifica-spediamo" onClick={() => verifica("spediamo")} className="text-xs font-semibold border border-slate-300 rounded px-2 py-1 hover:bg-slate-50">Verifica credenziali dedicate</button>
              {store.credenziali_view.spediamo.override && (
                <button data-testid="spediamo-override-rimuovi" onClick={rimuoviOverrideSpediamo} className="text-xs font-semibold text-red-600 border border-red-200 rounded px-2 py-1 hover:bg-red-50">Rimuovi credenziali dedicate</button>
              )}
            </div>
          </details>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Meta Ads — mappatura account → store</h3>
            <p className="text-[11px] text-slate-400 mb-1">Le credenziali Meta sono aziendali (sezione Integrazioni globali in alto). Qui associ solo gli ID account pubblicitari a questo store.</p>
            <label className="text-xs text-slate-500">ID account pubblicitari (separati da virgola — un account può servire più store)
              <input data-testid="cred-meta-ids" className={inp} placeholder="act_123456, act_789012" value={cred.meta_ids} onChange={(e) => setCred({ ...cred, meta_ids: e.target.value })} /></label>
          </div>
          <button data-testid="credenziali-save-btn" onClick={saveCred}
            className="bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-md px-4 py-2 transition-colors">Salva credenziali</button>
        </div>
      )}

      {store.parametri.map((p, i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid={`config-parametri-${i}`}>
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">Set parametri #{i + 1} <span className="text-slate-400 font-normal">(storicizzato)</span></h2>
            {store.parametri.length > 1 && (
              <button className="text-xs text-red-500" onClick={() => setStore((s) => ({ ...s, parametri: s.parametri.filter((_, j) => j !== i) }))}>Rimuovi</button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-4">
            <label className="text-xs text-slate-500">Valido da
              <input type="date" className={inp} value={p.valido_da || ""} onChange={(e) => setP(i, "valido_da", e.target.value)} /></label>
            <label className="text-xs text-slate-500">Valido a (vuoto = aperto)
              <input type="date" className={inp} value={p.valido_a || ""} onChange={(e) => setP(i, "valido_a", e.target.value || null)} /></label>
            <label className="text-xs text-slate-500">Aliquota IVA (%)
              <input data-testid={`config-aliquota-${i}`} type="number" step="0.1" className={inp} value={(p.aliquota_iva * 100).toFixed(2).replace(/\.?0+$/, "")}
                onChange={(e) => setP(i, "aliquota_iva", (parseFloat(e.target.value) || 0) / 100)} /></label>
          </div>
          <div data-testid={`config-costi-spedizione-${i}`}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Costi spedizione</h3>
            <p className="text-[11px] text-slate-400 mb-2">
              Addebitato = costo addebitato allo store: entra nel guadagno netto e nelle quote socio (il socio vede solo questo).
              Reale = costo pagato al corriere. La differenza è margine dell'azienda.
              Ogni voce è il costo del <strong>singolo evento</strong>: quando un pacco accumula più eventi, i costi <strong>si sommano</strong> (andata + giacenza + rientro + riconsegna).
            </p>
            {(() => {
              const cs = p.costi_spedizione || {};
              const a = cs.cod_consegnato || { addebitato: 0, reale: 0 };
              const r = cs.annullato_dopo_rientro || { addebitato: 0, reale: 0 };
              const fmt = (n) => (n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              return (
                <p data-testid={`config-esempio-spedizione-${i}`} className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-2 py-1.5 mb-2">
                  Esempio con questi valori — COD partito e rientrato: {fmt(a.addebitato)} + {fmt(r.addebitato)} = <strong>{fmt((a.addebitato || 0) + (r.addebitato || 0))} € addebitati</strong>, {fmt(a.reale)} + {fmt(r.reale)} = <strong>{fmt((a.reale || 0) + (r.reale || 0))} € reali</strong>
                </p>
              );
            })()}
            <div className="hidden sm:grid grid-cols-[1fr_140px_140px] gap-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
              <span>Evento</span><span>Addebitato (€)</span><span>Costo reale (€)</span>
            </div>
            <div className="space-y-2">
              {Object.entries(COSTI_LABELS).map(([k, label]) => (
                <div key={k} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_140px] gap-1.5 sm:gap-3 items-center">
                  <span className="text-xs text-slate-600">{label}</span>
                  <input data-testid={`config-costo-add-${k}`} type="number" step="0.01" className={inp} placeholder="Addebitato €"
                    value={p.costi_spedizione?.[k]?.addebitato ?? ""}
                    onChange={(e) => setPCosto(i, k, "addebitato", parseFloat(e.target.value) || 0)} />
                  <input data-testid={`config-costo-reale-${k}`} type="number" step="0.01" className={inp} placeholder="Reale €"
                    value={p.costi_spedizione?.[k]?.reale ?? ""}
                    onChange={(e) => setPCosto(i, k, "reale", parseFloat(e.target.value) || 0)} />
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Commissioni gateway per famiglia (%)</h3>
            <div className="grid grid-cols-6 gap-3">
              {FAMIGLIE.map((f) => (
                <label key={f} className="text-xs text-slate-500">{f} (%)
                  <div className="relative">
                    <input data-testid={`config-comm-fam-${f}`} type="number" step="0.1" className={inp + " pr-7"} value={((p.commissioni_famiglia?.[f] ?? 0) * 100).toFixed(2).replace(/\.?0+$/, "")}
                      onChange={(e) => setPCommFam(i, f, (parseFloat(e.target.value) || 0) / 100)} />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">%</span>
                  </div></label>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Commissioni per dettaglio (%, vuoto = usa famiglia)</h3>
            <div className="grid grid-cols-5 gap-3">
              {DETTAGLI.map((d) => (
                <label key={d} className="text-xs text-slate-500">{d} (%)
                  <div className="relative">
                    <input data-testid={`config-comm-det-${d}`} type="number" step="0.1" className={inp + " pr-7"} placeholder="—"
                      value={p.commissioni_dettaglio?.[d] != null ? (p.commissioni_dettaglio[d] * 100).toFixed(2).replace(/\.?0+$/, "") : ""}
                      onChange={(e) => setPCommDet(i, d, e.target.value === "" ? null : (parseFloat(e.target.value) || 0) / 100)} />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">%</span>
                  </div></label>
              ))}
            </div>
          </div>
        </div>
      ))}

      <div className="flex gap-3">
        <button data-testid="config-add-periodo" onClick={() => setStore((s) => ({ ...s, parametri: [...s.parametri, JSON.parse(JSON.stringify(s.parametri[s.parametri.length - 1]))] }))}
          className="border border-slate-300 text-slate-700 text-sm font-semibold rounded-md px-4 py-2 hover:bg-slate-50">+ Aggiungi periodo di validità</button>
        <button data-testid="store-config-save-btn" onClick={save}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md px-6 py-2 transition-colors">Salva configurazione</button>
      </div>
      <div className="card p-5" data-testid="sync-log-card">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-slate-800">Registro sincronizzazioni</h2>
          <button data-testid="sync-log-refresh" onClick={loadSyncLog} className="text-xs font-semibold border border-slate-300 rounded px-2 py-1 hover:bg-slate-50">Aggiorna</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-slate-100">
                {["Avvio", "Store", "Origine", "Modalità", "Progresso", "Creati", "Aggiornati", "Divergenze", "Errori", "Esito"].map((h) => (
                  <th key={h} className="py-1.5 pr-3 text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {syncLog.map((l) => (
                <tr key={l.id} data-testid={`sync-log-row-${l.id}`} className="border-b border-slate-50">
                  <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap">{new Date(l.avviata_il).toLocaleString("it-IT")}</td>
                  <td className="py-1.5 pr-3 text-xs">{l.store_nome || (l.tipo === "spedizioni-hub" ? "Spedizioni Hub" : "—")}</td>
                  <td className="py-1.5 pr-3 text-xs">{l.origine}</td>
                  <td className="py-1.5 pr-3 text-xs">{l.modalita}</td>
                  <td className="py-1.5 pr-3 text-xs" data-testid={`sync-log-progresso-${l.id}`}>
                    {l.stato === "in_corso" ? (
                      <span className="text-amber-700 whitespace-nowrap">
                        <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse mr-1" />
                        {l.tipo === "spedizioni-hub"
                          ? "Hub: lettura spedizioni…"
                          : l.progressi?.fase === "ordini"
                            ? `Ordini: ${l.progressi?.ordini_elaborati || 0} elaborati`
                            : `Prodotti: ${l.progressi?.prodotti_visti || 0} visti`}
                      </span>
                    ) : l.tipo === "spedizioni-hub" ? (
                      <span className="text-slate-500 whitespace-nowrap">
                        {l.spedizioni_lette || 0} spedizioni · {l.ordini_agganciati || 0} ordini · {l.costi_reali_applicati || 0} costi reali · {l.date_consegna_recuperate || 0} date consegna
                      </span>
                    ) : l.stato === "interrotta" ? (
                      <span className="text-orange-700">riprenderà da: {l.cursore?.fase || "inizio"}</span>
                    ) : (l.progressi?.ordini_elaborati || l.progressi?.prodotti_visti) ? (
                      <span className="text-slate-500 whitespace-nowrap">{l.progressi?.ordini_elaborati || 0} ordini · {l.progressi?.prodotti_visti || 0} prodotti</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                    {(l.retry_rete > 0 || l.retry_429 > 0) && (
                      <p data-testid={`sync-log-retry-${l.id}`} className="text-[11px] text-slate-400 mt-0.5 whitespace-nowrap">tentativi extra: {l.retry_rete || 0} rete · {l.retry_429 || 0} rate limit</p>
                    )}
                    {l.riprese > 0 && (
                      <p className="text-[11px] text-slate-400 mt-0.5">riprese: {l.riprese}</p>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-xs text-right">{l.creazioni}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs text-right">{l.aggiornati}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs text-right">{l.divergenze}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs text-right">{(l.errori || []).length}</td>
                  <td className="py-1.5 text-xs">
                    <span data-testid={`sync-log-esito-${l.id}`} className={`inline-flex rounded-full px-2 py-0.5 font-semibold ${l.stato === "ok" ? "bg-emerald-50 text-emerald-700" : l.stato === "in_corso" ? "bg-amber-50 text-amber-700" : l.stato === "interrotta" ? "bg-orange-50 text-orange-700" : l.stato === "incompleta" ? "bg-red-100 text-red-800 ring-2 ring-red-300 uppercase tracking-wide" : "bg-red-50 text-red-700"}`}>{l.stato === "incompleta" ? "⚠ incompleta" : l.stato}</span>
                    {l.nota_interruzione && <p data-testid={`sync-log-nota-${l.id}`} className="text-[11px] text-orange-700 mt-1 max-w-md">{l.nota_interruzione}</p>}
                    {l.rientri_rilevati > 0 && (
                      <p data-testid={`sync-log-rientro-${l.id}`} className="text-[11px] font-bold text-red-700 mt-1 max-w-md">
                        ⚠ {l.rientri_rilevati} spedizione/i con rientro rilevata/e: costo NON applicato, caso da verificare a mano
                      </p>
                    )}
                    {(l.avvisi || []).map((a, i) => (
                      <p key={i} data-testid={`sync-log-avviso-${l.id}`} className="text-[11px] text-amber-700 mt-1 max-w-md">{a}</p>
                    ))}
                    {(l.errori || []).slice(0, 3).map((e, i) => (
                      <p key={i} data-testid={`sync-log-errore-${l.id}`} className="text-[11px] text-red-700 mt-1 max-w-md">{e.ordine ? `${e.ordine}: ` : ""}{e.motivo}</p>
                    ))}
                  </td>
                </tr>
              ))}
              {syncLog.length === 0 && (
                <tr><td colSpan={10} className="py-4 text-center text-slate-400 text-xs">Nessuna sincronizzazione ancora eseguita.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <NuovoStoreDialog open={showNewStore} onClose={() => setShowNewStore(false)}
        masterStores={stores.filter((s) => s.tipo === "master")} />
    </div>
  );
}
